/**
 * The read-mostly pages: inbound, alerts, audit and reports. They share enough shape that keeping
 * them together makes the differences between them obvious.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type AlertRow, type InboundRow } from '../lib/api'
import { fmtAgo, fmtDateTime, fmtFull, fmtNumber, humanReason } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Empty, Loading, Pill } from '../components/ui'

/* ------------------------------------------------------------------ inbound */

export function Inbound() {
  const [from, setFrom] = useState('')

  const inbound = useQuery({
    queryKey: ['inbound', from],
    queryFn: () => api.get<InboundRow[]>('/api/admin/inbound', { from: from || undefined, limit: 150 }),
    refetchInterval: 8_000,
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Inbound</h1>
          <div className="page-sub">
            Messages received on the fleet's SIMs, decoded and routed to the owning source.
          </div>
        </div>
      </div>

      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Filter by sender…"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
      </div>

      <Card flush>
        {inbound.isLoading ? (
          <Loading />
        ) : inbound.data?.length === 0 ? (
          <Empty title="Nothing received yet" hint="Reply to one of the fleet's numbers and it will appear here." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>From</th>
                  <th>Message</th>
                  <th>Received on</th>
                  <th>Routed to</th>
                  <th>Auto-reply</th>
                </tr>
              </thead>
              <tbody>
                {inbound.data?.map((m) => (
                  <tr key={m.id}>
                    <td className="faint small" title={fmtFull(m.receivedAt)}>
                      {fmtDateTime(m.receivedAt)}
                    </td>
                    <td className="mono">{m.fromMsisdn}</td>
                    <td style={{ maxWidth: 380 }}>{m.body ?? <span className="faint">(empty)</span>}</td>
                    <td className="small">{m.clientName ?? '—'}</td>
                    <td className="small">
                      {m.sourceName ? (
                        <>
                          {m.sourceName}
                          {m.routingReason && <div className="faint small">via {m.routingReason}</div>}
                        </>
                      ) : (
                        <span className="faint">unrouted</span>
                      )}
                    </td>
                    <td>{m.autoReplied ? <Pill tone="ok">sent</Pill> : <span className="faint small">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------- alerts */

export function Alerts() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const [state, setState] = useState('open')

  const alerts = useQuery({
    queryKey: ['alerts', state],
    queryFn: () => api.get<AlertRow[]>('/api/admin/alerts', { state }),
    refetchInterval: 10_000,
  })

  const ack = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/alerts/${id}/ack`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const tone = (s: AlertRow['severity']) => (s === 'Critical' ? 'danger' : s === 'Warning' ? 'warn' : 'info')

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Alerts</h1>
          <div className="page-sub">
            Everything here opens and closes itself. Acknowledging only stops re-notification — it does not resolve
            the underlying condition.
          </div>
        </div>
      </div>

      <div className="tabs">
        {['open', 'resolved', 'all'].map((s) => (
          <button key={s} className={state === s ? 'tab active' : 'tab'} onClick={() => setState(s)}>
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {alerts.isLoading ? (
        <Loading />
      ) : alerts.data?.length === 0 ? (
        <Card>
          <Empty title={state === 'open' ? 'Nothing is wrong' : 'Nothing here'} />
        </Card>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {alerts.data?.map((a) => (
            <Card key={a.id}>
              <div className="row between" style={{ alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div className="row" style={{ gap: 9, marginBottom: 5 }}>
                    <Pill tone={tone(a.severity)}>{a.severity}</Pill>
                    <span className="mono small faint">{a.type}</span>
                    {a.occurrences > 1 && <span className="chip">×{a.occurrences}</span>}
                    {a.state === 'Acknowledged' && <span className="chip">acknowledged</span>}
                    {a.state === 'Resolved' && <Pill tone="ok">resolved</Pill>}
                  </div>
                  <strong>{a.title}</strong>
                  {a.detail && <div className="muted small" style={{ marginTop: 3 }}>{a.detail}</div>}
                  <div className="faint small" style={{ marginTop: 6 }}>
                    opened {fmtAgo(a.openedAt)}
                    {a.resolvedAt && ` · resolved ${fmtAgo(a.resolvedAt)}`}
                  </div>
                </div>
                {a.state === 'Open' && can('Operator') && (
                  <button className="btn sm" onClick={() => ack.mutate(a.id)}>
                    Acknowledge
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------- audit */

interface AuditRow {
  id: string
  actorType: string
  actorLabel: string | null
  action: string
  entityType: string | null
  entityId: string | null
  after: string | null
  ip: string | null
  at: string
}

export function Audit() {
  const [action, setAction] = useState('')

  const rows = useQuery({
    queryKey: ['audit', action],
    queryFn: () => api.get<AuditRow[]>('/api/admin/audit', { action: action || undefined, limit: 200 }),
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Audit</h1>
          <div className="page-sub">Every administrative change, who made it and from where.</div>
        </div>
      </div>

      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Filter by action, e.g. device. or user."
          value={action}
          onChange={(e) => setAction(e.target.value)}
        />
      </div>

      <Card flush>
        {rows.isLoading ? (
          <Loading />
        ) : rows.data?.length === 0 ? (
          <Empty title="No entries" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Detail</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {rows.data?.map((r) => (
                  <tr key={r.id}>
                    <td className="faint small" title={fmtFull(r.at)}>
                      {fmtDateTime(r.at)}
                    </td>
                    <td className="small">
                      {r.actorLabel ?? r.actorType}
                      <div className="faint small">{r.actorType}</div>
                    </td>
                    <td>
                      <span className="mono small">{r.action}</span>
                    </td>
                    <td className="faint small mono" style={{ maxWidth: 340, wordBreak: 'break-all' }}>
                      {r.after ?? '—'}
                    </td>
                    <td className="faint small mono">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------ reports */

interface ReportData {
  days: number
  byDay: { day: string; total: number; succeeded: number; failed: number }[]
  bySource: { sourceId: string; name: string; total: number; succeeded: number; failed: number }[]
  distribution: { id: string; name: string; weight: number; sent: number; actualPct: number; expectedPct: number }[]
  failureReasons: { reasonCode: string; count: number }[]
}

export function Reports() {
  const [days, setDays] = useState(7)

  const report = useQuery({
    queryKey: ['reports', days],
    queryFn: () => api.get<ReportData>('/api/admin/reports/summary', { days }),
  })

  if (report.isLoading || !report.data) return <Loading rows={8} />
  const { byDay, bySource, distribution, failureReasons } = report.data
  const maxDay = Math.max(1, ...byDay.map((d) => d.total))

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <div className="page-sub">Volume, per-source usage and whether the fleet is actually being used evenly.</div>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 150 }}>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      <Card title="Volume">
        <div className="spark" style={{ height: 90 }}>
          {byDay.map((d) => (
            <i
              key={d.day}
              style={{ height: `${Math.max(2, (d.total / maxDay) * 100)}%` }}
              title={`${fmtDateTime(d.day)} — ${d.total} total, ${d.failed} failed`}
            />
          ))}
        </div>
        <div className="row between faint small" style={{ marginTop: 8 }}>
          <span>{fmtDateTime(byDay[0]?.day)}</span>
          <span>{fmtNumber(byDay.reduce((a, b) => a + b.total, 0))} deliveries</span>
          <span>today</span>
        </div>
      </Card>

      <div className="grid cols-2">
        <Card title="Distribution fairness" flush>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Device</th>
                  <th className="num">Sent</th>
                  <th className="num">Actual</th>
                  <th className="num">Expected</th>
                </tr>
              </thead>
              <tbody>
                {distribution.map((d) => {
                  const drift = Math.abs(d.actualPct - d.expectedPct)
                  return (
                    <tr key={d.id}>
                      <td>{d.name}</td>
                      <td className="num">{fmtNumber(d.sent)}</td>
                      <td className="num" style={{ color: drift > 5 ? 'rgb(var(--warn))' : undefined }}>
                        {d.actualPct}%
                      </td>
                      <td className="num faint">{d.expectedPct}%</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="field-hint" style={{ padding: '10px 16px' }}>
            Actual share should track the weighted expectation. More than about five points of drift over a decent
            volume means routing is not spreading load the way it should.
          </div>
        </Card>

        <Card title="Failure reasons" flush>
          {failureReasons.length === 0 ? (
            <Empty title="No failures in this window" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th className="num">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {failureReasons.map((f) => (
                    <tr key={f.reasonCode}>
                      <td>{humanReason(f.reasonCode)}</td>
                      <td className="num">{fmtNumber(f.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card title="By source" flush>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Source</th>
                <th className="num">Total</th>
                <th className="num">Succeeded</th>
                <th className="num">Failed</th>
                <th className="num">Success rate</th>
              </tr>
            </thead>
            <tbody>
              {bySource.map((s) => {
                const rate = s.total === 0 ? 0 : Math.round((s.succeeded / s.total) * 100)
                return (
                  <tr key={s.sourceId}>
                    <td>{s.name}</td>
                    <td className="num">{fmtNumber(s.total)}</td>
                    <td className="num">{fmtNumber(s.succeeded)}</td>
                    <td className="num">{fmtNumber(s.failed)}</td>
                    <td className="num" style={{ color: rate < 90 ? 'rgb(var(--warn))' : 'rgb(var(--ok))' }}>
                      {rate}%
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
